import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, FileDown, Search, History, Users, ArrowRightLeft, RotateCcw, ClipboardList } from "lucide-react";
import { Attachments } from "@/components/Attachments";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/DatePicker";
import { exportToCsv } from "@/lib/exportCsv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { pdfHeader, trAscii } from "@/lib/pdf";
import { toast } from "sonner";

const EMPTY = { name: "", brand: "", model: "", serial_no: "", total_qty: 0, assigned_person: "", field: "", in_use_qty: 0, assigned_date: "", auto_total: 0 };

export default function Equipment() {
  const { user } = useAuth();
  const canEdit = user?.role !== "viewer";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");
  const [histOpen, setHistOpen] = useState(false);
  const [histEq, setHistEq] = useState(null);
  const [convOpen, setConvOpen] = useState(false);
  const [convEq, setConvEq] = useState(null);
  const [convCount, setConvCount] = useState(1);
  const [convDelete, setConvDelete] = useState(true);
  const [converting, setConverting] = useState(false);
  const [retOpen, setRetOpen] = useState(false);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledger, setLedger] = useState(null);

  const openLedger = async () => {
    setLedgerOpen(true); setLedger(null);
    try { const r = await api.get("/equipment/ledger"); setLedger(r.data); }
    catch (e) { toast.error("Zimmet defteri yüklenemedi"); }
  };
  const [retEq, setRetEq] = useState(null);
  const [retQty, setRetQty] = useState("");
  const [returning, setReturning] = useState(false);

  const openReturn = (r) => { setRetEq(r); setRetQty(""); setRetOpen(true); };
  const doReturn = async () => {
    const qv = parseInt(retQty, 10);
    if (!qv || qv <= 0) { toast.error("Geçerli bir adet girin"); return; }
    if (qv > (retEq.in_use_qty || 0)) { toast.error(`En fazla ${retEq.in_use_qty} adet iade edilebilir`); return; }
    setReturning(true);
    try {
      await api.post(`/equipment/${retEq.id}/return`, { quantity: qv });
      toast.success(`${retEq.name}: ${qv} adet iade alındı (kullanımda ${retEq.in_use_qty} → ${retEq.in_use_qty - qv})`);
      setRetOpen(false); load(q);
    } catch (e) { toast.error(e?.response?.data?.detail || "İade başarısız"); }
    finally { setReturning(false); }
  };
  const [assignments, setAssignments] = useState([]);
  const [rPerson, setRPerson] = useState("");
  const [rField, setRField] = useState("");
  const [rDate, setRDate] = useState("");
  const [rNote, setRNote] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkIds, setBulkIds] = useState([]);
  const [bPerson, setBPerson] = useState("");
  const [bField, setBField] = useState("");
  const [bDate, setBDate] = useState(new Date().toISOString().slice(0, 10));

  const toggleBulk = (id) => setBulkIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const bulkAssign = async (downloadPdf) => {
    if (bulkIds.length === 0) { toast.error("En az bir ekipman seçin"); return; }
    if (!bPerson.trim()) { toast.error("Personel girin"); return; }
    try {
      const resp = await api.post("/equipment/bulk-assign", { equipment_ids: bulkIds, assigned_person: bPerson, field: bField, assigned_date: bDate, note: "" });
      toast.success(`${resp.data?.count ?? bulkIds.length} ekipman zimmetlendi`);
      if (downloadPdf) bulkPdf();
      setBulkOpen(false); setBulkIds([]); setBPerson(""); setBField(""); load(q);
    } catch (e) { toast.error("İşlem başarısız"); }
  };

  const bulkPdf = () => {
    const t = trAscii;
    const items = rows.filter((r) => bulkIds.includes(r.id));
    const doc = new jsPDF();
    let hy = pdfHeader(doc);
    doc.setFontSize(18); doc.text(t("TOPLU EKİPMAN ZİMMET TUTANAĞI"), 105, hy + 8, { align: "center" });
    doc.setFontSize(11); let y = hy + 22;
    doc.setTextColor(110); doc.text(t("Zimmetlenen Personel:"), 20, y); doc.setTextColor(0); doc.text(t(bPerson), 80, y); y += 8;
    doc.setTextColor(110); doc.text(t("Saha / Lokasyon:"), 20, y); doc.setTextColor(0); doc.text(t(bField || "-"), 80, y); y += 8;
    doc.setTextColor(110); doc.text(t("Tarih:"), 20, y); doc.setTextColor(0); doc.text(t(bDate), 80, y); y += 6;
    autoTable(doc, {
      startY: y + 4,
      head: [["#", "Ekipman", "Marka/Model", "Seri No", "Adet"].map(t)],
      body: items.map((r, i) => [String(i + 1), t(r.name), t([r.brand, r.model].filter(Boolean).join(" ") || "-"), t(r.serial_no || "-"), String(r.total_qty)]),
      styles: { fontSize: 9 }, headStyles: { fillColor: [30, 58, 138] },
    });
    let fy = doc.lastAutoTable.finalY + 24;
    doc.setDrawColor(180);
    doc.text(t("Teslim Eden"), 40, fy + 8, { align: "center" }); doc.line(20, fy, 90, fy);
    doc.text(t("Teslim Alan"), 150, fy + 8, { align: "center" }); doc.line(120, fy, 190, fy);
    doc.save(`toplu_zimmet_${bPerson.replace(/\s+/g, "_")}.pdf`);
    toast.success("Toplu tutanak indirildi");
  };

  const openHist = async (r) => {
    setHistEq(r); setHistOpen(true); setAssignments([]);
    setRPerson(r.assigned_person || ""); setRField(r.field || ""); setRDate(r.assigned_date || ""); setRNote("");
    try { const res = await api.get(`/equipment/${r.id}/assignments`); setAssignments(res.data); } catch (e) {}
  };
  const reassign = async () => {
    try {
      const resp = await api.post(`/equipment/${histEq.id}/assignments`, { assigned_person: rPerson, field: rField, assigned_date: rDate, note: rNote });
      setHistEq(resp.data);
      toast.success("Zimmet güncellendi");
      const res = await api.get(`/equipment/${histEq.id}/assignments`); setAssignments(res.data);
      setRNote(""); load(q);
    } catch (e) { toast.error("İşlem başarısız"); }
  };

  const zimmetPdf = (a) => {
    const eq = histEq;
    const person = a ? a.assigned_person : rPerson;
    const field = a ? a.field : rField;
    const date = (a ? a.assigned_date : rDate) || new Date().toISOString().slice(0, 10);
    const note = a ? a.note : rNote;
    const t = trAscii;
    const doc = new jsPDF();
    let hy = pdfHeader(doc);
    doc.setFontSize(18); doc.setTextColor(0);
    doc.text(t("EKİPMAN ZİMMET TUTANAĞI"), 105, hy + 8, { align: "center" });
    doc.setFontSize(11);
    let y = hy + 24;
    const row = (k, v) => { doc.setTextColor(110); doc.text(t(k), 20, y); doc.setTextColor(0); doc.text(t(v || "-"), 80, y); y += 10; };
    row("Ekipman", `${eq?.name || ""}`);
    row("Marka / Model", `${[eq?.brand, eq?.model].filter(Boolean).join(" ") || "-"}`);
    row("Seri No", eq?.serial_no);
    row("Zimmetlenen Personel", person);
    row("Gonderildigi Saha", field);
    row("Zimmet Tarihi", date);
    if (note) row("Aciklama", note);
    y += 8;
    doc.setDrawColor(180);
    doc.text(t("Teslim Eden"), 40, y + 30, { align: "center" });
    doc.line(20, y + 22, 90, y + 22);
    doc.text(t("Teslim Alan"), 150, y + 30, { align: "center" });
    doc.line(120, y + 22, 190, y + 22);
    doc.setFontSize(9); doc.setTextColor(150);
    doc.text(t(`Bu tutanak ${new Date().toLocaleString("tr-TR")} tarihinde olusturulmustur.`), 105, 285, { align: "center" });
    doc.save(`zimmet_tutanagi_${(eq?.serial_no || eq?.name || "ekipman")}.pdf`);
    toast.success("Zimmet tutanağı indirildi");
  };

  const load = async (searchQ) => {
    setLoading(true);
    try { const res = await api.get("/equipment", { params: { q: (searchQ ?? q) || undefined } }); setRows(res.data); } finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(() => load(q), 300); return () => clearTimeout(t); }, [q]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (r) => {
    setForm({ name: r.name, brand: r.brand || "", model: r.model || "", serial_no: r.serial_no, total_qty: r.total_qty, assigned_person: r.assigned_person, field: r.field, in_use_qty: r.in_use_qty, assigned_date: r.assigned_date || "", auto_total: r.auto_total || 0 });
    setEditId(r.id); setOpen(true);
  };

  const filtered = rows;

  const int = (v) => parseInt(v) || 0;
  const save = async () => {
    if (!form.name) { toast.error("Ekipman adı zorunludur"); return; }
    const payload = { ...form, total_qty: int(form.total_qty), in_use_qty: int(form.in_use_qty) };
    if (payload.in_use_qty > payload.total_qty) { toast.error("Kullanımdaki adet toplamdan fazla olamaz"); return; }
    try {
      if (editId) { await api.put(`/equipment/${editId}`, payload); toast.success("Ekipman güncellendi"); }
      else { await api.post("/equipment", payload); toast.success("Ekipman eklendi"); }
      setOpen(false); load(q);
    } catch (e) { toast.error("İşlem başarısız"); }
  };

  const remove = async (id) => { await api.delete(`/equipment/${id}`); toast.success("Silindi"); load(q); };

  const openConvert = (r) => { setConvEq(r); setConvCount(r.total_qty || 1); setConvDelete(true); setConvOpen(true); };
  const doConvert = async () => {
    if (!convEq) return;
    setConverting(true);
    try {
      const res = await api.post(`/equipment/${convEq.id}/convert-to-machine`, { count: parseInt(convCount, 10) || 1, delete_source: convDelete });
      toast.success(`${res.data.count} makine kaydı oluşturuldu (${res.data.codes.join(", ")})`);
      setConvOpen(false);
      load(q);
    } catch (e) { toast.error("Aktarım başarısız"); }
    finally { setConverting(false); }
  };

  const exportCsv = () => {
    if (rows.length === 0) { toast.info("Aktarılacak veri yok"); return; }
    exportToCsv("ekipman_zimmet", [
      { key: "name", label: "Ekipman Adı" },
      { key: "brand", label: "Marka" },
      { key: "model", label: "Model" },
      { key: "serial_no", label: "Seri No" },
      { key: "total_qty", label: "Toplam Adet" },
      { key: "assigned_person", label: "Zimmetli Personel" },
      { key: "field", label: "Saha / Lokasyon" },
      { key: "assigned_date", label: "Zimmet Tarihi" },
      { key: "in_use_qty", label: "Kullanımdaki Adet" },
      { key: "in_stock_qty", label: "Depoda Kalan" },
    ], filtered);
  };

  return (
    <Layout
      title="Ekipman Zimmet & Saha Takibi"
      subtitle="Zimmetli personel, gönderildiği saha ve depo bakiyesi"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid="ledger-btn" onClick={openLedger} variant="outline" className="rounded-sm gap-2">
            <ClipboardList className="w-4 h-4" /> Zimmet Defteri
          </Button>
          {canEdit && (
          <Button data-testid="bulk-assign-btn" onClick={() => { setBulkOpen(true); setBulkIds([]); setBPerson(""); setBField(""); setBDate(new Date().toISOString().slice(0, 10)); }} variant="outline" className="rounded-sm gap-2">
            <Users className="w-4 h-4" /> Toplu Zimmet
          </Button>
          )}
          <Button data-testid="export-equipment-btn" onClick={exportCsv} variant="outline" className="rounded-sm gap-2">
            <FileDown className="w-4 h-4" /> Excel'e Aktar
          </Button>
          {canEdit && (
          <Button data-testid="add-equipment-btn" onClick={openNew} className="rounded-sm gap-2 bg-primary hover:bg-primary/90">
            <Plus className="w-4 h-4" /> Ekipman Ekle
          </Button>
          )}
        </div>
      }
    >
      <div className="mb-4 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="equipment-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Personel, saha, marka, model, seri no ara... (örn. Sami Şenel, Kocaeli)" className="pl-9 rounded-sm bg-white" />
      </div>
      <Card className="rounded-sm border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50">
              <TableHead className="text-xs font-bold uppercase tracking-wide">Ekipman</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Marka / Model</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Seri No</TableHead>
              <TableHead className="text-right text-xs font-bold uppercase tracking-wide">Toplam</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Zimmetli Personel</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Saha / Lokasyon</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Zimmet Tarihi</TableHead>
              <TableHead className="text-right text-xs font-bold uppercase tracking-wide">Kullanımda</TableHead>
              <TableHead className="text-right text-xs font-bold uppercase tracking-wide">Depoda</TableHead>
              <TableHead className="text-right text-xs font-bold uppercase tracking-wide">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={10} className="text-center py-10 text-muted-foreground">Yükleniyor...</TableCell></TableRow>}
            {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-10 text-muted-foreground">Kayıt bulunamadı</TableCell></TableRow>}
            {filtered.map((r) => (
              <TableRow key={r.id} data-testid={`equipment-row-${r.serial_no || r.name}`} className="odd:bg-white even:bg-zinc-50/50">
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="text-sm">{[r.brand, r.model].filter(Boolean).join(" ") || "-"}</TableCell>
                <TableCell className="font-mono text-sm text-muted-foreground">{r.serial_no || "-"}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{r.total_qty}</TableCell>
                <TableCell>{r.assigned_person || "-"}</TableCell>
                <TableCell>{r.field || "-"}</TableCell>
                <TableCell className="font-mono text-sm">{r.assigned_date ? r.assigned_date.split("-").reverse().join(".") : "-"}</TableCell>
                <TableCell className="text-right font-mono text-amber-700">{r.in_use_qty}</TableCell>
                <TableCell className="text-right font-mono font-bold text-emerald-700">{r.in_stock_qty}</TableCell>
                <TableCell className="text-right">
                  <Button data-testid={`history-equipment-${r.serial_no || r.name}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-secondary" onClick={() => openHist(r)} title="Zimmet Geçmişi & Dosyalar"><History className="w-4 h-4" /></Button>
                  {canEdit && <>
                  <Button data-testid={`convert-equipment-${r.serial_no || r.name}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-primary" onClick={() => openConvert(r)} title="Makineye Aktar (tekil takip)"><ArrowRightLeft className="w-4 h-4" /></Button>
                  {r.in_use_qty > 0 && (
                    <Button data-testid={`return-equipment-${r.serial_no || r.name}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-blue-700" onClick={() => openReturn(r)} title="İade Al (kullanımdan düş)"><RotateCcw className="w-4 h-4" /></Button>
                  )}
                  <Button data-testid={`edit-equipment-${r.serial_no || r.name}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm" onClick={() => openEdit(r)}><Pencil className="w-4 h-4" /></Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button data-testid={`delete-equipment-${r.serial_no || r.name}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-red-600"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                    <AlertDialogContent className="rounded-sm">
                      <AlertDialogHeader><AlertDialogTitle>Ekipman silinsin mi?</AlertDialogTitle><AlertDialogDescription>{r.name} silinecek.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Vazgeç</AlertDialogCancel><AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700" onClick={() => remove(r.id)}>Sil</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  </>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Ekipman Düzenle" : "Yeni Ekipman"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <Field label="Ekipman Adı *"><Input data-testid="eqp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" /></Field>
            <Field label="Seri No"><Input data-testid="eqp-serial" value={form.serial_no} onChange={(e) => setForm({ ...form, serial_no: e.target.value })} className="rounded-sm" /></Field>
            <Field label="Marka"><Input data-testid="eqp-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="rounded-sm" /></Field>
            <Field label="Model"><Input data-testid="eqp-model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="rounded-sm" /></Field>
            <Field label="Toplam Adet">
              <Input data-testid="eqp-total" type="number" value={form.total_qty} disabled={!!form.auto_total} onChange={(e) => setForm({ ...form, total_qty: e.target.value })} className="rounded-sm disabled:bg-zinc-100 disabled:text-zinc-500" />
              {form.auto_total ? (
                <p className="text-[11px] text-emerald-700 mt-1">🔒 Otomatik: "{form.name}" adlı <b>{form.auto_total}</b> tekil kayıt sayıldı (Makine/Ekipman modülü). Yeni eklemek için oraya kayıt açın.</p>
              ) : (
                <p className="text-[11px] text-muted-foreground mt-1">Bu ekipmandan <b>elinizdeki toplam</b> sayı. (Makine/Ekipman modülüne aynı isimle tekil kayıt açarsanız otomatik sayılır.)</p>
              )}
            </Field>
            <Field label="Kullanımdaki Adet">
              <Input data-testid="eqp-inuse" type="number" value={form.in_use_qty} onChange={(e) => setForm({ ...form, in_use_qty: e.target.value })} className="rounded-sm" />
              <p className="text-[11px] text-muted-foreground mt-1">Şu an <b>personelde/sahada zimmetli</b> olan sayı. Depoda kalan: <b>{Math.max(0, (parseInt(form.total_qty, 10) || 0) - (parseInt(form.in_use_qty, 10) || 0))}</b></p>
            </Field>
            <Field label="Zimmetlenen Personel"><Input data-testid="eqp-person" value={form.assigned_person} onChange={(e) => setForm({ ...form, assigned_person: e.target.value })} className="rounded-sm" /></Field>
            <Field label="Gönderildiği Saha"><Input data-testid="eqp-field" value={form.field} onChange={(e) => setForm({ ...form, field: e.target.value })} className="rounded-sm" /></Field>
            <Field label="Zimmet Tarihi"><DatePicker testid="eqp-date" value={form.assigned_date} onChange={(v) => setForm({ ...form, assigned_date: v })} /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>İptal</Button>
            <Button data-testid="save-equipment-btn" className="rounded-sm bg-primary hover:bg-primary/90" onClick={save}>Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={ledgerOpen} onOpenChange={setLedgerOpen}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ledger-modal">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-primary" /> Zimmet Defteri
            </DialogTitle>
          </DialogHeader>
          {!ledger && <p className="py-8 text-center text-muted-foreground text-sm">Yükleniyor...</p>}
          {ledger && (
            <div className="space-y-4 py-1">
              <p className="text-xs text-muted-foreground">{ledger.person_count} kişide toplam {ledger.total_qty} adet ekipman zimmetli. (Şu an kullanımdaki adetler)</p>
              {ledger.people.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">Şu an kimsede zimmetli ekipman yok.</p> : ledger.people.map((p) => (
                <div key={p.person} data-testid={`ledger-person-${p.person}`} className="rounded-sm border border-border p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold flex items-center gap-1.5"><Users className="w-4 h-4 text-secondary" /> {p.person}</span>
                    <span className="font-mono font-black text-primary">{p.total_qty} adet</span>
                  </div>
                  <Table><TableBody>
                    {p.items.map((it, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-sm py-1.5">{it.name}{it.serial_no ? <span className="text-xs text-muted-foreground ml-1.5">#{it.serial_no}</span> : ""}</TableCell>
                        <TableCell className="text-xs text-muted-foreground py-1.5">{it.field || "-"}</TableCell>
                        <TableCell className="text-right font-mono font-semibold py-1.5">{it.qty}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody></Table>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={retOpen} onOpenChange={setRetOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-blue-700" /> İade Al
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-muted-foreground">
              <strong>{retEq?.name}</strong> — şu an kullanımda: <strong>{retEq?.in_use_qty}</strong> adet{retEq?.assigned_person ? ` (${retEq.assigned_person})` : ""}. İade edilen adet kadar kullanımdan düşer, depoya eklenir.
            </p>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">İade Adedi *</Label>
              <Input data-testid="return-qty" type="number" min="1" max={retEq?.in_use_qty} value={retQty} onChange={(e) => setRetQty(e.target.value)} className="rounded-sm mt-1" placeholder={`En fazla ${retEq?.in_use_qty}`} />
              {retQty && !isNaN(parseInt(retQty, 10)) && (
                <p className="text-[11px] text-muted-foreground mt-1">İade sonrası kullanımda: <b>{Math.max(0, (retEq?.in_use_qty || 0) - parseInt(retQty, 10))}</b> · depoda: <b>{(retEq?.in_stock_qty || 0) + Math.min(retEq?.in_use_qty || 0, parseInt(retQty, 10) || 0)}</b></p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setRetOpen(false)}>İptal</Button>
            <Button data-testid="return-confirm-btn" disabled={returning} className="rounded-sm bg-blue-600 hover:bg-blue-700 text-white" onClick={doReturn}>{returning ? "İade ediliyor..." : "İade Al"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={convOpen} onOpenChange={setConvOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-primary" /> Makineye Aktar
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-muted-foreground">
              <strong>{convEq?.name}</strong> ekipmanını "Makine, Araç & Ekipman" modülüne <strong>tekil kayıt</strong> olarak aktarır. Her kayıt kendi kodu, durumu, bakım geçmişi ve muayene tarihleriyle takip edilir.
            </p>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Kaç adet kayıt oluşturulsun?</Label>
              <Input data-testid="convert-count" type="number" min="1" max="100" value={convCount} onChange={(e) => setConvCount(e.target.value)} className="rounded-sm mt-1" />
              <p className="text-[11px] text-muted-foreground mt-1">Örn. 3 adet matkap için "3" girin; kodlar otomatik numaralanır.</p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input data-testid="convert-delete" type="checkbox" checked={convDelete} onChange={(e) => setConvDelete(e.target.checked)} />
              Aktarımdan sonra bu zimmet kaydını sil
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setConvOpen(false)}>İptal</Button>
            <Button data-testid="convert-confirm-btn" disabled={converting} className="rounded-sm bg-primary hover:bg-primary/90" onClick={doConvert}>{converting ? "Aktarılıyor..." : "Aktar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <History className="w-5 h-5 text-secondary" />
              Zimmet Geçmişi & Dosyalar {histEq ? `— ${histEq.name}` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-4">
            {canEdit && (
            <div className="bg-zinc-50 rounded-sm p-3 border border-border">
              <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Yeniden Zimmetle</p>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
                <div><Label className="text-[10px] uppercase text-muted-foreground">Personel</Label><Input data-testid="reassign-person" value={rPerson} onChange={(e) => setRPerson(e.target.value)} className="rounded-sm mt-1" /></div>
                <div><Label className="text-[10px] uppercase text-muted-foreground">Saha</Label><Input data-testid="reassign-field" value={rField} onChange={(e) => setRField(e.target.value)} className="rounded-sm mt-1" /></div>
                <div><Label className="text-[10px] uppercase text-muted-foreground">Tarih</Label><div className="mt-1"><DatePicker testid="reassign-date" value={rDate} onChange={setRDate} /></div></div>
                <Button data-testid="reassign-btn" onClick={reassign} className="rounded-sm bg-primary hover:bg-primary/90">Zimmetle</Button>
              </div>
              <div className="flex gap-2 mt-2">
                <Input data-testid="reassign-note" value={rNote} onChange={(e) => setRNote(e.target.value)} placeholder="Not (opsiyonel)" className="rounded-sm flex-1" />
                <Button data-testid="zimmet-pdf-btn" onClick={() => zimmetPdf(null)} variant="outline" className="rounded-sm gap-2 shrink-0"><FileDown className="w-4 h-4" /> PDF Tutanak</Button>
              </div>
            </div>
            )}

            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Zimmet Geçmişi</p>
              <div className="border border-border rounded-sm overflow-hidden max-h-[220px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-zinc-50">
                      <TableHead className="text-xs font-bold uppercase">Tarih</TableHead>
                      <TableHead className="text-xs font-bold uppercase">İşlem</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Personel</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Saha</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Not / Adet</TableHead>
                      <TableHead className="text-right text-xs font-bold uppercase">Tutanak</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assignments.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">Kayıt yok</TableCell></TableRow>}
                    {assignments.map((a) => {
                      const isIade = (a.note || "").toLocaleLowerCase("tr").startsWith("i̇ade") || (a.note || "").startsWith("İade");
                      return (
                      <TableRow key={a.id} data-testid={`assignment-row-${a.id}`} className={isIade ? "bg-blue-50/60" : "odd:bg-white even:bg-zinc-50/50"}>
                        <TableCell className="font-mono text-xs whitespace-nowrap">{a.assigned_date ? a.assigned_date.split("-").reverse().join(".") : new Date(a.timestamp).toLocaleDateString("tr-TR")}</TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase ${isIade ? "bg-blue-100 text-blue-700 border border-blue-200" : "bg-emerald-100 text-emerald-700 border border-emerald-200"}`}>{isIade ? "İade" : "Zimmet"}</span>
                        </TableCell>
                        <TableCell className="text-sm">{a.assigned_person || "-"}</TableCell>
                        <TableCell className="text-sm">{a.field || "-"}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{a.note || "-"}</TableCell>
                        <TableCell className="text-right">
                          <Button data-testid={`assignment-pdf-${a.id}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-secondary" onClick={() => zimmetPdf(a)} title="Tutanak İndir"><FileDown className="w-4 h-4" /></Button>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <Attachments entityType="equipment" entityId={histEq?.id} />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Users className="w-5 h-5 text-secondary" /> Toplu Zimmet
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Personel *</Label>
                <Input data-testid="bulk-person" value={bPerson} onChange={(e) => setBPerson(e.target.value)} placeholder="Örn. Mehmet Demir" className="rounded-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Saha / Lokasyon</Label>
                <Input data-testid="bulk-field" value={bField} onChange={(e) => setBField(e.target.value)} placeholder="Örn. Kapıkule Sahası" className="rounded-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Zimmet Tarihi</Label>
                <DatePicker testid="bulk-date" value={bDate} onChange={setBDate} />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Ekipman Seçimi</p>
                <span className="text-xs text-muted-foreground" data-testid="bulk-selected-count">{bulkIds.length} seçili</span>
              </div>
              <div className="border border-border rounded-sm max-h-[300px] overflow-y-auto divide-y divide-border">
                {rows.length === 0 && <p className="text-center py-6 text-muted-foreground text-sm">Ekipman bulunamadı</p>}
                {rows.map((r) => (
                  <label key={r.id} data-testid={`bulk-item-${r.serial_no || r.name}`} className="flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-50 cursor-pointer">
                    <Checkbox data-testid={`bulk-check-${r.serial_no || r.name}`} checked={bulkIds.includes(r.id)} onCheckedChange={() => toggleBulk(r.id)} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{r.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{[r.brand, r.model].filter(Boolean).join(" ")} {r.serial_no ? `· ${r.serial_no}` : ""}</p>
                    </div>
                    <span className="text-xs font-mono text-muted-foreground shrink-0">{r.total_qty} adet</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setBulkOpen(false)}>İptal</Button>
            <Button data-testid="bulk-assign-pdf" variant="outline" className="rounded-sm gap-2" onClick={() => bulkAssign(true)}>
              <FileDown className="w-4 h-4" /> Zimmetle + PDF
            </Button>
            <Button data-testid="bulk-assign-submit" className="rounded-sm bg-primary hover:bg-primary/90" onClick={() => bulkAssign(false)}>
              Zimmetle ({bulkIds.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function Field({ label, children }) {
  return (<div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{label}</Label>{children}</div>);
}
